import {
  subclass,
  property,
  aliasOf,
} from "@arcgis/core/core/accessorSupport/decorators";

import Widget from "@arcgis/core/widgets/Widget";
import t9nAppMessages from "../../../t9n/App/App.json";

import { messageBundle, tsx } from "@arcgis/core/widgets/support/widget";
import Collection from "@arcgis/core/core/Collection";

import "./FilterChips.scss";
import FilterChipsViewModel from "./FilterChipsViewModel";
import { on } from "@arcgis/core/core/reactiveUtils";

export type filterType = "capability" | "category";

export interface IChipState {
  guid: string;
  text: string;
  filterType: filterType;
}

export const SAVED_SEARCHES_KEY = "savedSearches";

@subclass("Landing.widgets.FilterChips")
export default class FilterChips extends Widget {
  //--------------------------------------------------------------------------
  //
  //  Properties
  //
  //--------------------------------------------------------------------------

  @property()
  viewModel: FilterChipsViewModel = new FilterChipsViewModel();

  @property()
  @aliasOf("viewModel.chips")
  chips: __esri.Collection<IChipState> = new Collection();

  /** This is a ref from NavbarVM */
  @property()
  filterByList: __esri.Collection<[string, string]>;

  @property()
  @messageBundle("dist/assets/t9n/App/App")
  appMessages: typeof t9nAppMessages;

  //--------------------------------------------------------------------------
  //
  //  Lifecycle Methods
  //
  //--------------------------------------------------------------------------

  postInitialize(): void {
    on(
      () => this.filterByList,
      "change",
      () => {
        if (this.filterByList.length > 0) {
          const keys = this.filterByList.map(
            ([translationKey, searchTerm]) => translationKey
          );
          localStorage.setItem(SAVED_SEARCHES_KEY, JSON.stringify(keys));
        }
      }
    );
  }

  render() {
    const chipsToRender = this.chips
      ?.map((chip) => {
        return this._renderChip(chip);
      })
      .toArray();
    return (
      <div class="filter-chips">
        {chipsToRender.length > 0
          ? chipsToRender
          : this.appMessages.filterChips.noChips}
      </div>
    );
  }

  //--------------------------------------------------------------------------
  //
  //  Private Methods
  //
  //--------------------------------------------------------------------------
  private _renderChip(chip: IChipState) {
    return (
      <calcite-chip
        key={`capability-chip-${chip.text}`}
        scale="m"
        appearance="solid"
        kind="neutral"
        label={chip.text}
        closable={true}
        afterCreate={(chipDOM: HTMLCalciteChipElement) => {
          chipDOM.addEventListener("calciteChipClose", () => {
            const chipToDismiss = this.chips.find((testChip) => {
              return (
                chip.text === testChip.text &&
                chip.filterType === testChip.filterType
              );
            });
            if (chipToDismiss) {
              this._handleDismiss(chipToDismiss);
            }
          });
        }}
      >
        {this.appMessages.filterChips[chip.filterType]}: {chip.text}
      </calcite-chip>
    );
  }

  private _handleDismiss(chipToDismiss: IChipState) {
    const toRemove = this.filterByList.find(([translationKey, searchTerm]) => {
      return chipToDismiss.text === searchTerm;
    });
    this.filterByList.remove(toRemove);
    setTimeout(() => {
      const foundChip = this.chips.find(
        (chip) => chip.guid === chipToDismiss.guid
      );
      this.chips.remove(foundChip);
    }, 500);
  }
}
